import React, { useState, useEffect } from "react";
import { FaCalendarCheck } from "react-icons/fa";
import { FaProjectDiagram } from "react-icons/fa";
import { FaFileDownload } from "react-icons/fa";
import * as XLSX from "xlsx";
import ProjectView from "./ProjectView";
import {
  Box,
  HStack,
  VStack,
  IconButton,
  Heading,
  Badge,
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  Text,
  Select,
  Spinner,
  Button,
  Tooltip,
  useColorMode,
} from "@chakra-ui/react";
import {
  FaArrowLeft,
  FaListUl,
  FaChartLine,
  FaCalendarDay,
  FaUserTie,
  FaChevronLeft,
} from "react-icons/fa";
import { format, parseISO } from "date-fns";
import { collection, getDoc, doc, getDocs } from "firebase/firestore";
import { db } from "../../../firebase/firebase";
import TimesheetGraph from "./TimesheetGraph";
import TimesheetCalendar from "./TimesheetCalendar";

import { IconWrapper } from "../../../components/IconWrapper";
import { getThemeClasses } from "../../../utils/themeUtils";
import CustomDropdown from '../../../components/CustomDropdown';
import { formatDateDDMMYYYY } from "../../../utils/dateUtils";
import { showToast } from "../../../utils/toastUtils";

interface TimesheetEntry {
  date: string;
  hours: string;
  jobCode: string;
  project: string;
  status: string;
  taskDescription: string;
  timestamp: string;
  rejectionReason?: string;
}

interface Project {
  id: string;
  name: string;
}

interface UserDetailsPageProps {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    role: string;
    assignedProjects?: string[];
  };
  organizationId: string;
  onBack: () => void;
  color: string;
  bg: string;
}

const UserDetailsPage: React.FC<UserDetailsPageProps> = ({
  user,
  organizationId,
  onBack,
  color,
  bg,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);
  const [timesheets, setTimesheets] = useState<TimesheetEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [viewMode, setViewMode] = useState<
    "table" | "graph" | "calendar" | "project"
  >("table");

  const [selectedYear, setSelectedYear] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState({
    title: "",
    message: "",
    type: "",
  });




  // Fetch projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        if (!user.assignedProjects?.length) {
          setProjects([]);
          return;
        }

        const projectsData = await Promise.all(
          user.assignedProjects.map(async (projectId) => {
            const projectDoc = await getDoc(
              doc(db, `organization/${organizationId}/projects/${projectId}`)
            );
            if (projectDoc.exists()) {
              const data = projectDoc.data();
              return {
                id: projectId,
                name: data.name || projectId,
              };
            }
            return {
              id: projectId,
              name: projectId, // Fallback to ID if project not found
            };
          })
        );
        console.log("Fetched projects:", projectsData); // Debug log
        setProjects(projectsData);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setProjects([]);
      }
    };

    fetchProjects();
  }, [organizationId, user.assignedProjects]);

  // Fetch timesheets
  useEffect(() => {
    const fetchTimesheets = async () => {
      setLoading(true);
      try {
        const entries: TimesheetEntry[] = [];
        const seenKeys = new Set<string>();

        // 1. Weekly architecture: TimeSheetsData/{userId}/weeks/{weekMonday}
        const tsColRef = collection(db, "organization", organizationId, "TimeSheetsData", user.id, "weeks");
        const snap = await getDocs(tsColRef);
        snap.docs.forEach(wkDoc => {
          const wkData = wkDoc.data();
          const weekEntries = wkData.entries || [];
          weekEntries.forEach((entry: any) => {
            entries.push(entry);
            const key = `${entry.date}-${entry.project}-${entry.hours}-${entry.timestamp}`;
            seenKeys.add(key);
          });
        });

        // 2. Legacy path
        const oldRef = doc(db, "organization", organizationId, "timesheets", user.id);
        const oldSnap = await getDoc(oldRef);
        if (oldSnap.exists()) {
          const legacyEntries = oldSnap.data().entries || [];
          legacyEntries.forEach((entry: any) => {
            const key = `${entry.date}-${entry.project}-${entry.hours}-${entry.timestamp}`;
            if (!seenKeys.has(key)) {
              entries.push(entry);
            }
          });
        }
        
        console.log("Fetched Data:", entries);
        setTimesheets(entries);
      } catch (error) {
        console.error("Error fetching timesheets:", error);
        setTimesheets([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTimesheets();
  }, [organizationId, user.id]);

  // 🟡 Filter Logic
  const filteredTimesheets = timesheets
    .filter((entry) => {
      const entryDate = parseISO(entry.date);
      const entryYear = entryDate.getFullYear().toString();
      const entryMonth = (entryDate.getMonth() + 1).toString().padStart(2, "0");

      return (
        (!selectedYear || entryYear === selectedYear) &&
        (!selectedMonth || entryMonth === selectedMonth) &&
        (!selectedStatus ||
          entry.status.toLowerCase() === selectedStatus.toLowerCase())
      );
    })
    .sort((a, b) => b.date.localeCompare(a.date)); // Latest date first

  // 🟢 Count Status for Filtered Data
  const statusCounts = filteredTimesheets.reduce(
    (counts, entry) => {
      const raw = entry.status.toLowerCase();
      const status =
        raw === "approve" ? "approved" : raw === "reject" ? "rejected" : raw;

      // 2. increment the right bucket
      counts[status] = (counts[status] || 0) + 1;
      return counts;
    },
    { approved: 0, pending: 0, rejected: 0 } as Record<string, number>
  );

  // 🧹 Reset Filters
  const handleReset = () => {
    setSelectedYear("");
    setSelectedMonth("");
    setSelectedStatus("");
  };

  // 📅 Get Unique Years from Timesheets
  const uniqueYears = Array.from(
    new Set(timesheets.map((entry) => parseISO(entry.date).getFullYear()))
  ).sort((a, b) => b - a);

  // Show toast notification
  const showNotification = (
    title: string,
    message: string,
    type: "success" | "error"
  ) => {
    setToastMessage({ title, message, type });
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  // 📥 Export to Excel
  const handleExportToExcel = () => {
    try {
      // Prepare data for Excel
      const data = filteredTimesheets.map((entry) => ({
        Date: formatDateDDMMYYYY(entry.date),
        Hours: entry.hours,
        "Job Code": entry.jobCode,
        Project: entry.project,
        Status: entry.status,
        "Task Description": entry.taskDescription,
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Timesheet");

      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `timesheet_${user.firstName}_${user.lastName}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Show success notification
      showNotification(
        "Export Successful",
        "Timesheet has been exported successfully!",
        "success"
      );
    } catch (error) {
      // Show error notification
      showNotification(
        "Export Failed",
        "Failed to export timesheet. Please try again.",
        "error"
      );
      console.error("Export error:", error);
    }
  };

  return (
    <Box
      borderRadius="lg"
      bg={bg}
      color={color}
      position={"relative"}
      top={"5px"}
      px={5}
      py={2}
    >
      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in">
          <div
            className={`rounded-md shadow-lg p-4 ${toastMessage.type === "success"
              ? "bg-green-50 border-l-4 border-green-500 text-green-700"
              : "bg-red-50 border-l-4 border-red-500 text-red-700"
              }`}
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {toastMessage.type === "success" ? (
                  <svg
                    className="h-5 w-5 text-green-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium">{toastMessage.title}</p>
                <p className="text-sm">{toastMessage.message}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header Section with Back Button & View Mode Icons */}
      <HStack justifyContent="space-between" mb={6}>
        <HStack>
          <Button
            leftIcon={<IconWrapper icon={FaArrowLeft} size={16} />}
            onClick={onBack}
            variant="gold"
            size="xs"
          >
            Back
          </Button>
        </HStack>

        {/* View Mode Switcher */}
        <HStack position={"relative"} top={"-2px"}>
          <Tooltip label="Table view">
            <IconButton
              icon={<IconWrapper icon={FaListUl} size={16} />}
              borderRadius={"full"}
              size={"sm"}
              aria-label="Table View"
              onClick={() => setViewMode("table")}
              variant="outline"
              bg="transparent"
              color="#B59143"
              border="1px solid"
              borderColor="#B59143"
              _hover={{ bg: isDark ? "rgba(42,52,65,0.6)" : "#FFF7E6" }}
            />
          </Tooltip>
          <Tooltip label="Graph View">
            <IconButton
              icon={<IconWrapper icon={FaChartLine} size={16} />}
              borderRadius={"full"}
              size={"sm"}
              aria-label="Graph View"
              onClick={() => setViewMode("graph")}
              variant="outline"
              bg="transparent"
              color="#B59143"
              border="1px solid"
              borderColor="#B59143"
              _hover={{ bg: isDark ? "rgba(42,52,65,0.6)" : "#FFF7E6" }}
            />
          </Tooltip>
          <Tooltip label="Calendar View">
            <IconButton
              icon={<IconWrapper icon={FaCalendarDay} size={16} />}
              aria-label="Calendar View"
              borderRadius={"full"}
              size={"sm"}
              onClick={() => setViewMode("calendar")}
              variant="outline"
              bg="transparent"
              color="#B59143"
              border="1px solid"
              borderColor="#B59143"
              _hover={{ bg: isDark ? "rgba(42,52,65,0.6)" : "#FFF7E6" }}
            />
          </Tooltip>
          <Tooltip label="Project View">
            <IconButton
              icon={<IconWrapper icon={FaUserTie} size={16} />}
              aria-label="Project View"
              borderRadius="full"
              size="sm"
              onClick={() => setViewMode("project")}
              variant="outline"
              bg="transparent"
              color="#B59143"
              border="1px solid"
              borderColor="#B59143"
              _hover={{ bg: isDark ? "rgba(42,52,65,0.6)" : "#FFF7E6" }}
            />
          </Tooltip>
        </HStack>
      </HStack>

      {/* User Info */}
      {viewMode === "table" && (
        <HStack
          spacing={5}
          alignItems="center"
          justify="space-between"
          width="100%"
          mb={5}
          flexWrap="wrap"
        >
          {/* Name and Role */}
          <HStack spacing={3}>
            <Text fontSize="lg" fontWeight="bold" className={`${isDark ? 'text-white' : 'text-gray-900'}`}>
              {user.firstName} {user.lastName}
            </Text>
            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${isDark ? 'border border-[#B59143] text-[#FBD282]' : 'bg-[#FFF7E6] text-[#B59143] border border-[#E8C16C]'}`}>
              {user.role}
            </span>
          </HStack>

          {/* Email and Phone */}
          <HStack spacing={5}>
            <HStack spacing={2}>
              <Text fontSize="sm" fontWeight="bold" className={`${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>
                📧
              </Text>
              <Text fontSize="sm" className={`${isDark ? 'text-white' : 'text-gray-900'}`}>
                {user.email}
              </Text>
            </HStack>

            <HStack spacing={2}>
              <Text fontSize="sm" fontWeight="bold" className={`${isDark ? 'text-[#C3C9D3]' : 'text-gray-600'}`}>
                📞
              </Text>
              <Text fontSize="sm" className={`${isDark ? 'text-white' : 'text-gray-900'}`}>
                {user.phone || "N/A"}
              </Text>
            </HStack>
          </HStack>
        </HStack>
      )}

      {/* Content Based on Selected View */}
      {loading ? (
        <HStack justify="center" my={5}>
          <Spinner size="lg" color="teal.500" />
        </HStack>
      ) : viewMode === "table" ? (
        <VStack spacing={4} align="stretch">
          {/* 🛠️ Filter and Status Summary */}
          <HStack justify="space-between" mb={2} wrap="wrap" className={`rounded-md p-3 border ${theme.border} ${theme.card}`}>
            {/* Year Filter */}
            <HStack>
              <Text fontSize="sm" fontWeight="bold" className={`${isDark ? 'text-white' : 'text-gray-900'}`}>Year:</Text>
              <CustomDropdown
                value={selectedYear}
                onChange={setSelectedYear}
                isDark={isDark}
                options={[
                  { value: '', label: 'All Years' },
                  ...uniqueYears.map(year => ({ value: year.toString(), label: year.toString() }))
                ]}
                className="w-[120px] px-3 py-1.5 text-sm rounded-md border focus:outline-none focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143]"
              />
            </HStack>
            <HStack>
              <Text fontSize="sm" fontWeight="bold" className={`${isDark ? 'text-white' : 'text-gray-900'}`}>Month:</Text>
              <CustomDropdown
                value={selectedMonth}
                onChange={setSelectedMonth}
                isDark={isDark}
                options={[
                  { value: '', label: 'All Months' },
                  ...Array.from({ length: 12 }, (_, i) => ({ value: (i + 1).toString().padStart(2, '0'), label: format(new Date(0, i), 'MMMM') }))
                ]}
                className="w-[120px] px-3 py-1.5 text-sm rounded-md border focus:outline-none focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143]"
              />
            </HStack>
            <HStack>
              <Text fontSize="sm" fontWeight="bold" className={`${isDark ? 'text-white' : 'text-gray-900'}`}>Status:</Text>
              <CustomDropdown
                value={selectedStatus}
                onChange={setSelectedStatus}
                isDark={isDark}
                options={[
                  { value: '', label: 'All' },
                  { value: 'approved', label: 'Approved' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'rejected', label: 'Rejected' }
                ]}
                className="w-[120px] px-3 py-1.5 text-sm rounded-md border focus:outline-none focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143]"
              />
            </HStack>

            <Button size="xs" variant="outline" className={`${isDark ? 'text-white' : 'text-gray-700'}`} onClick={handleReset}>
              Reset Filters
            </Button>
            <Tooltip label="Export Data">
              <IconButton
                icon={
                  <IconWrapper
                    icon={FaFileDownload}
                    size={16}



                  />

                }
                size="xs"
                variant="outline"
                bg={isDark ? "transparent" : "#FFF7E6"}
                color="#B59143"
                border="1px solid"
                borderColor="#B59143"
                _hover={{ bg: isDark ? "rgba(42,52,65,0.6)" : "#FFF7E6" }}
                onClick={handleExportToExcel}
                aria-label="Download Excel"
              />
            </Tooltip>
            {/* Status Summary */}
            <HStack>
              <Badge colorScheme="green" fontSize="0.6em" px={2} py={1}>
                ✅ Approved: {statusCounts.approved}
              </Badge>
              <Badge colorScheme="yellow" fontSize="0.6em" px={2} py={1}>
                🟡 Pending: {statusCounts.pending}
              </Badge>
              <Badge colorScheme="red" fontSize="0.6em" px={2} py={1}>
                ❌ Rejected: {statusCounts.rejected}
              </Badge>
            </HStack>

            {/* Reset Button */}
          </HStack>

          {/* 🟣 Scrollable Table with Truncated Text */}
          <Box height="calc(100vh - 240px)" overflowX="auto" overflowY="auto" className={`${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-white border border-gray-200'} rounded-md`}>
            {(() => {
              // Apply Period Filtering first (No longer needed, using year/month filters)
              let items = filteredTimesheets;

              if (items.length === 0) {
                return (
                  <VStack py={10} spacing={2}>
                    <Text fontStyle="italic" color="gray.500">No timesheet records found for the selected period.</Text>
                  </VStack>
                );
              }

              // Group by date
              const grouped: Record<string, TimesheetEntry[]> = {};
              items.forEach(e => {
                const d = e.date;
                if (!grouped[d]) grouped[d] = [];
                grouped[d].push(e);
              });

              const sortedDates = Object.keys(grouped).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
              let grandTotal = 0;
              let sNoCounter = 1;

              return (
                <Table variant="simple" size="sm" minWidth="1000px" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                  <Thead position="sticky" top={0} zIndex={10} bg={isDark ? '#14161B' : 'gray.50'}>
                    <Tr h="45px" verticalAlign="middle">
                      <Th width="60px" minWidth="60px" whiteSpace="nowrap" border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} color={isDark ? 'white' : 'gray.800'} textAlign="center" fontSize="xs">S No</Th>
                      <Th width="80px" minWidth="80px" whiteSpace="nowrap" border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} color={isDark ? 'white' : 'gray.800'} fontSize="xs">Date</Th>
                      <Th width="170px" border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} color={isDark ? 'white' : 'gray.800'} fontSize="xs">Project</Th>
                      <Th width="170px" border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} color={isDark ? 'white' : 'gray.800'} fontSize="xs">Job Code</Th>
                      <Th border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} color={isDark ? 'white' : 'gray.800'} fontSize="xs">Description</Th>
                      <Th width="100px" border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} color={isDark ? 'white' : 'gray.800'} textAlign="center" fontSize="xs">Hours</Th>
                      <Th width="100px" border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} color={isDark ? 'white' : 'gray.800'} textAlign="center" fontSize="xs">Total</Th>
                      <Th width="150px" border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} color={isDark ? 'white' : 'gray.800'} fontSize="xs">Comments</Th>
                      <Th width="120px" border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} color={isDark ? 'white' : 'gray.800'} fontSize="xs">Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sortedDates.map((dateStr) => {
                      const dateEntries = grouped[dateStr];
                      const dailyTotal = dateEntries.reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0);
                      grandTotal += dailyTotal;
                      const currentSNo = sNoCounter++;

                      return dateEntries.map((entry, idx) => (
                        <Tr key={`${dateStr}-${idx}`} className={isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-gray-50'}>
                          {idx === 0 && (
                            <>
                              <Td rowSpan={dateEntries.length} border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} textAlign="center" fontWeight="bold" color={isDark ? 'white' : 'black'} fontSize="xs" whiteSpace="nowrap">
                                {currentSNo}
                              </Td>
                              <Td rowSpan={dateEntries.length} border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} fontWeight="medium" color={isDark ? 'white' : 'black'} fontSize="xs" whiteSpace="nowrap">
                                {format(new Date(dateStr), 'MMM dd')}
                              </Td>
                            </>
                          )}
                          <Td border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} color={isDark ? 'white' : 'gray.800'} fontSize="xs" whiteSpace="pre-wrap" lineHeight="tight">{entry.project}</Td>
                          <Td border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} color={isDark ? 'white' : 'gray.800'} fontSize="xs" whiteSpace="pre-wrap" lineHeight="tight">{entry.jobCode}</Td>
                          <Td border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} color={isDark ? 'white' : 'gray.800'} fontSize="xs" lineHeight="tight" style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                            {entry.taskDescription || '-'}
                          </Td>
                          <Td border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} textAlign="center" color={isDark ? 'white' : 'gray.800'} fontSize="xs">{entry.hours}</Td>
                          {idx === 0 && (
                            <Td rowSpan={dateEntries.length} border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} textAlign="center" fontWeight="bold" color={isDark ? 'white' : 'black'} fontSize="xs">
                              {dailyTotal}
                            </Td>
                          )}
                          <Td border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} color={isDark ? 'white' : 'gray.800'} fontSize="xs" lineHeight="tight" style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                            {entry.rejectionReason || '-'}
                          </Td>
                          <Td border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'}>
                            <HStack spacing={1}>
                              <Badge
                                colorScheme={
                                  entry.status.toLowerCase() === "approved" || entry.status.toLowerCase() === "approve"
                                    ? "green"
                                    : entry.status.toLowerCase() === "pending"
                                      ? "yellow"
                                      : "red"
                                }
                                fontSize="0.65em"
                                variant="subtle"
                              >
                                {entry.status}
                              </Badge>
                            </HStack>
                          </Td>
                        </Tr>
                      ));
                    })}
                  </Tbody>
                  <Tfoot position="sticky" bottom={0} zIndex={10} bg={isDark ? '#14161B' : 'gray.100'} borderTop="2px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'}>
                    <Tr h="40px">
                      <Td colSpan={5} border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} textAlign="right" fontWeight="bold" color={isDark ? 'white' : 'black'} textTransform="uppercase" letterSpacing="wider" fontSize="xs">
                        Grand Total:
                      </Td>
                      <Td border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} textAlign="center" fontWeight="bold" color={isDark ? 'white' : 'black'} fontSize="xs"></Td>
                      <Td border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'} textAlign="center" fontWeight="black" color="#B59143" fontSize="sm" bg={isDark ? '#B59143/5' : '#FFF7E6'}>
                        {grandTotal.toFixed(1)}
                      </Td>
                      <Td border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'}></Td>
                      <Td border="1px solid" borderColor={isDark ? '#2E2F33' : 'gray.300'}></Td>
                    </Tr>
                  </Tfoot>
                </Table>
              );
            })()}
          </Box>
        </VStack>
      ) : viewMode === "graph" ? (
        // 📈 **Graph View**
        <Box>
          <TimesheetGraph timesheets={timesheets} />
        </Box>
      ) : viewMode === "calendar" ? (
        // 🗓 **Calendar View**
        <Box >
          <TimesheetCalendar color={color} bg={bg} timesheets={timesheets} />
        </Box>
      ) : viewMode === "project" ? (
        <Box >
          <ProjectView timesheets={timesheets} assignedProjects={projects} />
        </Box>
      ) : null}
    </Box>
  );
};

export default UserDetailsPage;
